// @flow

import React, { useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import * as sharedSelectors from "@modules/shared/selectors";
import actionCreators from "@modules/service/actionCreator";
import { SectionComponent, Button, Col, Row, View } from "@components";
import type { State } from "@store/reducers";
import {
  AbsenceMonitoringSettingsView,
  AbsenceMonitoringLevelsView,
  CommonReasonInputView,
  AbsenceMonitoringActionsView,
  AbsenceMonitoringEditModal,
  AppealDetailsView,
} from "@modules/service/components";
import type { AlertShort } from "@modules/shared/reducer/alerts/types";
import { CommonComplianceKpisView } from "@modules/service/components/common/common-compliance-kpis";

type OwnProps = $Exact<{
  data: Object,
  schema: Object,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  alerts: Array<AlertShort>,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreators.SetAbsenceMonitoringDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

const AbsenceMonitoringDetailsView = (props: Props) => {
  const [step, setStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const handleEditStep = (step: number) => () => {
    setStep(step);
    props.setDraft(props.data);
    toggleModal();
  };

  const getSchema = () => {
    const { schema } = props;
    if (!schema) {
      return {};
    }
    return {
      noActionReasons: (((schema || {}).properties || {}).noActionReasons || {}).items,
      additionalSupportActions: (((schema || {}).properties || {}).additionalSupportActions || {})
        .items,
      kpis: (((schema || {}).properties || {}).kpis || {}).items,
      appealReasons: schema.properties?.appealReasons?.items || [],
      outcomeReasons: schema.properties?.outcomeReasons?.items || [],
    };
  };

  if (!props.data) {
    return null;
  }

  return (
    <React.Fragment>
      <View direction="row" gap={4}>
        {/* Process Settings */}
        <View className="gs-container" flex={1}>
          <SectionComponent
            title={<h6>{props.t("ABSENCE_MONITORING_DETAILS_SETTINGS")}</h6>}
            rightLink={
              <Button materialIcon="edit" onClick={handleEditStep(1)}>
                {props.t("EDIT")}
              </Button>
            }
          >
            <AbsenceMonitoringSettingsView data={props.data} />
          </SectionComponent>
        </View>

        {/* Compliance Settings */}
        <View className="gs-container" flex={1}>
          <SectionComponent
            title={<h6>{props.t("COMPLIANCE")}</h6>}
            rightLink={
              <Button materialIcon="edit" onClick={handleEditStep(2)}>
                {props.t("EDIT")}
              </Button>
            }
          >
            <CommonComplianceKpisView
              data={props.data?.content.kpis}
              schema={getSchema().kpis}
              kpiTitle={props.t("KPI_TARGET_HOURS")}
            />
          </SectionComponent>
        </View>
      </View>
      {/* Levels Settings */}
      <View className="gs-container gs-wrapper" flex={1}>
        <SectionComponent
          title={<h6>{props.t("ABSENCE_MONITORING_DETAILS_MONITORING_LEVELS")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(3)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <AbsenceMonitoringLevelsView
            data={props.data?.content}
            alerts={props.alerts}
            tasks={(props.data?.content || {}).tasks}
          />
        </SectionComponent>
      </View>
      {/* Lists Settings */}
      <View className="gs-container gs-wrapper" flex={1}>
        <SectionComponent
          title={<h6>{props.t("ACTION_LISTS")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(4)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <View gap={6} direction="row">
            <CommonReasonInputView
              title={props.t("ABSENCE_MONITORING_DETAILS_REASONS_FOR_NO_ACTION")}
              data={(props.data?.content || {}).noActionReasons}
              schema={(getSchema() || {}).noActionReasons}
            />

            <AbsenceMonitoringActionsView
              title={props.t("ABSENCE_MONITORING_DETAILS_REASONS_ADDITIONAL_SUPPORT_ACTIONS")}
              data={(props.data?.content || {}).additionalSupportActions}
              schema={(getSchema() || {}).additionalSupportActions}
            />
          </View>
        </SectionComponent>
      </View>

      <View className="gs-container gs-wrapper" flex={1}>
        <SectionComponent
          title={<h6>{props.t("APPEALS")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(5)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <View gap={6}>
            <AppealDetailsView data={props.data} />
            {props.data.content.appeal?.isActive && (
              <View direction="row" gap={4}>
                <CommonReasonInputView
                  title={props.t("REASON_FOR_APPEAL")}
                  data={props.data.content?.appealReasons}
                  schema={(getSchema() || {}).appealReasons}
                  titleField="name"
                />
                <CommonReasonInputView
                  title={props.t("APPEAL_HEARING_OUTCOME")}
                  data={props.data.content?.outcomeReasons}
                  schema={(getSchema() || {}).outcomeReasons}
                  titleField="name"
                />
              </View>
            )}
          </View>
        </SectionComponent>
      </View>
      {isModalOpen && props.schema && (
        <AbsenceMonitoringEditModal
          clientId={props.clientId}
          defaultStep={step}
          editing={isModalOpen}
          onCancel={toggleModal}
        />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state: State): PropsFromState => ({
  alerts: sharedSelectors.getAlerts(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreators.SetAbsenceMonitoringDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceMonitoringDetailsView);
